import React, { useMemo, useRef, useState } from 'react';

import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from '../../icons';
import { useOutsideAlerter } from '../../utils/useOutsideAlerter';

import {
  Container,
  PageButton,
  PagesContainer,
  SkipButton,
  TakeButton,
  TakeContainer,
  TakeDropdown,
  TakeSelector,
} from './Pagination.styles';
import { PaginationProps } from './Pagination.types';

export const Pagination: React.FC<PaginationProps> = props => {
  const { numPages, currentPage, onChange, taking, disabled } = props;

  const takeRef = useRef<HTMLButtonElement>(null);

  useOutsideAlerter(takeRef, () => setOpenTakeDropdown(false));

  const [openTakeDropdown, setOpenTakeDropdown] = useState(false);

  const pageArray: (number | '<' | '>')[] = useMemo(() => {
    const array = Array.from(Array(numPages + 1).keys()).slice(1);
    const n = array.length;

    if (numPages === 0) {
      return [1];
    }

    if (numPages <= 5) return array;

    if (currentPage <= 3) return [1, 2, 3, 4, '>', array[n - 1]];
    if (currentPage >= n - 2)
      return [1, '<', array[n - 4], array[n - 3], array[n - 2], array[n - 1]];

    return [1, '<', currentPage - 1, currentPage, currentPage + 1, '>', array[n - 1]];
  }, [currentPage, numPages]);

  const changePage = (page: number) => {
    if (disabled) return;
    onChange(page);
  };

  const paginate = (step: number) => {
    const newPosition = currentPage + step;
    if (newPosition > numPages) {
      changePage(numPages);
      return;
    }
    if (newPosition <= 1) {
      changePage(1);
      return;
    }
    changePage(newPosition);
  };

  const openTakeDropDown = () => {
    if (disabled) return;
    setOpenTakeDropdown(prevValue => !prevValue);
  };

  return (
    <Container data-testid="pagination" style={props.style} disabled={disabled}>
      <PagesContainer>
        <SkipButton data-testid="left-button" onClick={() => paginate(-1)}>
          <ChevronLeft />
        </SkipButton>
        {pageArray.map(page =>
          page !== '<' && page !== '>' ? (
            <PageButton
              key={page}
              onClick={() => changePage(page)}
              selected={page === currentPage}
              paginationDisabled={disabled}
            >
              {page}
            </PageButton>
          ) : page === '<' ? (
            <SkipButton data-testid="skip-left" key={'skip-minus-5'} onClick={() => paginate(-5)}>
              <b>...</b>
            </SkipButton>
          ) : (
            <SkipButton data-testid="skip-right" key={'skip-plus-5'} onClick={() => paginate(5)}>
              <b>...</b>
            </SkipButton>
          ),
        )}
        <SkipButton data-testid="right-button" onClick={() => paginate(1)}>
          <ChevronRight />
        </SkipButton>
      </PagesContainer>
      {taking && (
        <TakeContainer>
          Itens por página:
          <TakeSelector
            data-testid="take-selector"
            type="button"
            ref={takeRef}
            onClick={openTakeDropDown}
          >
            {taking.take}
            {openTakeDropdown ? <ChevronUp /> : <ChevronDown />}
            {openTakeDropdown && (
              <TakeDropdown data-testid="take-dropdown">
                {[5, 10, 15, 20]
                  .filter(number => number !== taking.take)
                  .map(number => (
                    <TakeButton key={number} onClick={() => taking.onChange(number)}>
                      {number}
                    </TakeButton>
                  ))}
              </TakeDropdown>
            )}
          </TakeSelector>
        </TakeContainer>
      )}
    </Container>
  );
};
